import React, { ComponentType } from 'react'
import { useMatchScreenRedesign } from '../context/RedesignConfigContext'
import type { ModuleKey } from '../moduleRegistry'

/**
 * HOC that gates a module component behind its Optimizely variable key.
 *
 * At render time the returned component applies two guards in order:
 * 1. If `moduleKey` is absent from `window.matchScreenRedesign.variables`,
 *    return null.
 * 2. If the variable value is an object with `enabled === false`, return null.
 * Otherwise the wrapped component renders normally and receives all props
 * unchanged.
 *
 * The guard reads `variables` from context rather than accepting it as a prop
 * so every guarded module stays in sync with the same frozen snapshot that
 * was set by the server before React mounted.
 *
 * `displayName` is set explicitly so the component appears as
 * `withModuleGuard(HeadingModule)` in React DevTools and error boundaries
 * rather than as an anonymous function.
 *
 * @example
 * // Wrap once at module scope — not inside render.
 * const GuardedHeading = withModuleGuard(HeadingModule, MODULE_KEYS.heading)
 */
export function withModuleGuard<P extends object> (
  WrappedComponent: ComponentType<P>,
  moduleKey: ModuleKey
): ComponentType<P> {
  const displayName = WrappedComponent.displayName ?? WrappedComponent.name
  const GuardedComponent = (props: P) => {
    const { variables } = useMatchScreenRedesign()
    const moduleVar = variables[moduleKey]
    if (!(moduleKey in variables)) return null
    if (typeof moduleVar === 'object' && moduleVar !== null && (moduleVar as Record<string, unknown>).enabled === false) return null
    return <WrappedComponent {...props} />
  }
  GuardedComponent.displayName = `withModuleGuard(${displayName})`
  return GuardedComponent
}
